import { IconUtils } from '@/common/utils/IconUtils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Autocomplete, InputAdornment, TextField } from '@mui/material';
import { useMemo } from 'react';
import { useFormFieldDef } from '../hooks/useFormFieldDef';

export const EnumSelect = ({ fieldDef, shouldDisplayCode }) => {
  const { value, setValue, onBlur, error, helperText, label, disabled, variant } = useFormFieldDef(fieldDef);
  // ENTITY_ENUM stores the FK id; plain enums store the code
  const key = fieldDef.type === 'ENTITY_ENUM' ? 'id' : 'code';

  const keyToEnumSpec = useMemo(
    () =>
      fieldDef.values.reduce((map, enumSpec) => {
        map[enumSpec[key]] = enumSpec;
        return map;
      }, {}),
    [fieldDef.values, key],
  );

  const selectedOption = useMemo(() => keyToEnumSpec[value] ?? null, [keyToEnumSpec, value]);

  // build the icon node once
  let selectedIcon = null;
  if (selectedOption?.icon) {
    selectedIcon = (
      <InputAdornment position="start">
        <FontAwesomeIcon
          icon={IconUtils.iconFromIconStr(selectedOption.icon)}
          color={selectedOption.color}
          spin={selectedOption.icon.includes('spinner')}
        />
      </InputAdornment>
    );
  }

  return (
    <Autocomplete
      fullWidth
      options={fieldDef.values}
      getOptionLabel={(option) => (shouldDisplayCode ? `${option.code}: ${option.displayName}` : option.displayName)}
      value={selectedOption}
      onChange={(_, newVal) => setValue(newVal ? newVal[key] : undefined)}
      onBlur={onBlur}
      disabled={disabled}
      disableClearable
      renderOption={({ key, ...optionProps }, option) => (
        <li key={key} {...optionProps}>
          {option.icon && (
            <FontAwesomeIcon
              style={{ marginRight: 6 }}
              icon={IconUtils.iconFromIconStr(option.icon)}
              color={option.color}
              spin={option.icon.includes('spinner')}
            />
          )}
          {shouldDisplayCode ? `${option.code}: ${option.displayName}` : option.displayName}
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          fullWidth
          label={label}
          error={error}
          helperText={helperText}
          onBlur={onBlur}
          variant={variant}
          slotProps={{
            input: {
              ...params.InputProps,
              // stick our selected‐value icon in front of the text
              startAdornment: selectedIcon ?? params.InputProps.startAdornment,
            },
          }}
        />
      )}
    />
  );
};
